/**
 * DisplaySettingsMenu · v2.8.5
 * ──────────────────────────────
 * A single header control (popover) that groups the console's view options:
 *   · Density  — Comfort / Compact / Auto (screen-size aware)
 *   · Text Size — Small / Medium / Large
 *
 * Purely presentational — state lives in the parent (via useDisplayPrefs) so
 * the parent can apply the resulting `.ui-compact` / `.ui-scale-*` classes to
 * its own root element. Shared by the Admin Console and the Driver app.
 */
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal, Type } from "lucide-react";

function Segment({ options, value, onChange, testIdPrefix }) {
  return (
    <div className="grid grid-cols-3 gap-1" role="group">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            data-testid={`${testIdPrefix}-${o.value}`}
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            className={`text-xs font-semibold py-1.5 rounded-md border transition-colors ${
              active
                ? "border-amber-400 bg-amber-400/15 text-amber-600 dark:text-amber-300"
                : "border-border bg-transparent text-muted-foreground hover:text-foreground hover:border-amber-400/40"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default function DisplaySettingsMenu({
  compact,
  autoCompact,
  fontScale,
  effectiveCompact,
  setCompact,
  setAutoCompact,
  setFontScale,
  buttonClassName = "",
  align = "end",
}) {
  const densityValue = autoCompact ? "auto" : compact ? "compact" : "comfort";
  const onDensity = (v) => {
    if (v === "auto") {
      setAutoCompact(true);
    } else if (v === "compact") {
      setAutoCompact(false);
      setCompact(true);
    } else {
      setAutoCompact(false);
      setCompact(false);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          data-testid="display-settings-trigger"
          variant="outline"
          size="sm"
          className={`h-8 text-xs gap-1 ${buttonClassName}`}
          title="Display settings — spacing & text size"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Display</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-64" data-testid="display-settings-panel">
        <div className="space-y-4">
          {/* Density */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                Density
              </span>
              {autoCompact && (
                <span
                  className="text-[10px] text-amber-600 dark:text-amber-300 font-semibold"
                  data-testid="display-auto-indicator"
                >
                  Auto · {effectiveCompact ? "Compact" : "Comfort"}
                </span>
              )}
            </div>
            <Segment
              options={[
                { value: "comfort", label: "Comfort" },
                { value: "compact", label: "Compact" },
                { value: "auto", label: "Auto" },
              ]}
              value={densityValue}
              onChange={onDensity}
              testIdPrefix="display-density"
            />
            <p className="text-[10px] text-muted-foreground mt-1.5">
              {densityValue === "auto"
                ? "Adapts to screen size — tightens on small screens."
                : densityValue === "compact"
                ? "Tighter spacing to fit more on screen."
                : "Roomy, comfortable spacing."}
            </p>
          </div>

          {/* Text size */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Type className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                Text Size
              </span>
            </div>
            <Segment
              options={[
                { value: "sm", label: "Small" },
                { value: "md", label: "Medium" },
                { value: "lg", label: "Large" },
              ]}
              value={fontScale}
              onChange={setFontScale}
              testIdPrefix="display-font"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
